import { useState } from 'react';
import { summarizeTelemetry } from '../../analysis/telemetry';
import { RULES_VERSION } from '../../engine';
import { t } from '../../i18n';
import { APP_BUILT_AT, APP_COMMIT_SHORT, APP_VERSION } from '../../app/version';
import { INSCRIPTION_POLICIES, THEMES, type AiSpeed, type Settings } from '../../app/settings';
import { downloadJson } from '../../app/saves';
import { matchLogStatus } from '../../app/matchLog';
import { clearTelemetry, loadTelemetry } from '../../app/telemetryStore';
import { prefersReducedMotion } from '../../app/settings';

function Toggle({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}) {
  return (
    <label className="toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="toggle-track" aria-hidden="true" />
      <span className="toggle-label">
        {label}
        {hint && <span className="field-hint">{hint}</span>}
      </span>
    </label>
  );
}

export function SettingsScreen({
  settings,
  onChange,
  onBack,
}: {
  settings: Settings;
  onChange: (s: Settings) => void;
  onBack: () => void;
}) {
  const [telemetryCount, setTelemetryCount] = useState(() => loadTelemetry().length);
  const [sharing] = useState(matchLogStatus);
  const sharedWhen = sharing.lastSharedAt ? new Date(sharing.lastSharedAt).toLocaleString() : '';
  const set = <K extends keyof Settings>(key: K, value: Settings[K]) =>
    onChange({ ...settings, [key]: value });
  return (
    <main className="page">
      <header className="page-head">
        <button type="button" className="btn btn-small" onClick={onBack}>
          ← {t('menu.back')}
        </button>
        <h1>{t('settings.title')}</h1>
      </header>
      <div className="settings-list">
        <fieldset className="choice">
          <legend>{t('settings.theme')}</legend>
          <div className="choice-options">
            {THEMES.map((theme) => (
              <label
                key={theme}
                className={`choice-option theme-option ${settings.theme === theme ? 'is-checked' : ''}`}
              >
                <input
                  type="radio"
                  name="theme"
                  checked={settings.theme === theme}
                  onChange={() => set('theme', theme)}
                />
                <span className={`theme-swatch theme-swatch-${theme}`} aria-hidden="true" />
                <span className="choice-label">{t(`settings.theme.${theme}`)}</span>
                <span className="choice-hint">{t(`settings.theme.${theme}.hint`)}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <Toggle
          label={t('settings.confirmActions')}
          checked={settings.confirmActions}
          onChange={(v) => set('confirmActions', v)}
        />
        <Toggle
          label={t('settings.reducedMotion')}
          checked={settings.reducedMotion ?? prefersReducedMotion()}
          onChange={(v) => set('reducedMotion', v)}
        />
        <Toggle
          label={t('settings.showPreview')}
          checked={settings.showPreview}
          onChange={(v) => set('showPreview', v)}
        />
        <Toggle
          label={t('settings.highContrast')}
          checked={settings.highContrast}
          onChange={(v) => set('highContrast', v)}
        />
        <Toggle
          label={t('settings.flipHotseat')}
          checked={settings.flipHotseat}
          onChange={(v) => set('flipHotseat', v)}
        />
        <Toggle
          label={t('settings.sound')}
          checked={settings.sound}
          onChange={(v) => set('sound', v)}
        />
        <label className="field">
          <span className="field-label">{t('settings.volume')}</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={settings.volume}
            disabled={!settings.sound}
            onChange={(e) => set('volume', Number(e.target.value))}
          />
        </label>
        <fieldset className="choice">
          <legend>{t('settings.aiSpeed')}</legend>
          <p className="field-hint">{t('settings.aiSpeedHint')}</p>
          <div className="choice-options">
            {(['fast', 'normal', 'slow'] as const).map((speed: AiSpeed) => (
              <label
                key={speed}
                className={`choice-option ${settings.aiSpeed === speed ? 'is-checked' : ''}`}
              >
                <input
                  type="radio"
                  name="ai-speed"
                  checked={settings.aiSpeed === speed}
                  onChange={() => set('aiSpeed', speed)}
                />
                <span className="choice-label">{t(`settings.aiSpeed.${speed}`)}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset className="choice">
          <legend>{t('settings.inscriptions')}</legend>
          <p className="field-hint">{t('settings.inscriptionsHint')}</p>
          <div className="choice-options">
            {INSCRIPTION_POLICIES.map((policy) => (
              <label
                key={policy}
                className={`choice-option ${settings.inscriptions === policy ? 'is-checked' : ''}`}
              >
                <input
                  type="radio"
                  name="inscriptions"
                  checked={settings.inscriptions === policy}
                  onChange={() => set('inscriptions', policy)}
                />
                <span className="choice-label">{t(`settings.inscriptions.${policy}`)}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <Toggle
          label={t('settings.shareMatches')}
          hint={t('settings.shareMatchesHint')}
          checked={settings.shareMatches}
          onChange={(v) => set('shareMatches', v)}
        />
        {(sharing.shared > 0 || sharing.waiting > 0) && (
          <p className="field-hint share-status">
            {sharing.shared === 1
              ? t('settings.shareStatusOne', { when: sharedWhen })
              : sharing.shared > 1
                ? t('settings.shareStatus', { n: sharing.shared, when: sharedWhen })
                : ''}
            {sharing.waiting > 0 && ` ${t('settings.shareWaiting', { n: sharing.waiting })}`}
          </p>
        )}
        <Toggle
          label={t('settings.telemetry')}
          hint={t('settings.telemetryHint')}
          checked={settings.telemetry}
          onChange={(v) => set('telemetry', v)}
        />
        <div className="form-actions">
          <button
            type="button"
            className="btn"
            disabled={telemetryCount === 0}
            onClick={() => {
              const matches = loadTelemetry();
              downloadJson(`pawned-telemetry-${new Date().toISOString().slice(0, 10)}.json`, {
                summary: summarizeTelemetry(matches),
                matches,
              });
            }}
          >
            {t('settings.exportTelemetry', { n: telemetryCount })}
          </button>
          <button
            type="button"
            className="btn"
            disabled={telemetryCount === 0}
            onClick={() => {
              clearTelemetry();
              setTelemetryCount(0);
            }}
          >
            {t('settings.clearTelemetry')}
          </button>
        </div>
        <section className="about">
          <h2 className="mini-label">{t('settings.about')}</h2>
          <p className="dim">
            {t('app.build', {
              version: APP_VERSION,
              rules: RULES_VERSION,
              commit: APP_COMMIT_SHORT,
              date: APP_BUILT_AT ? `, ${new Date(APP_BUILT_AT).toLocaleDateString()}` : '',
            })}
          </p>
        </section>
      </div>
    </main>
  );
}
